import React, { useState, useEffect } from 'react';
import { 
  Modal, 
  Steps, 
  Button, 
  Upload, 
  message, 
  Typography, 
  Descriptions, 
  Table, 
  Alert,
  Space,
  Result,
  Spin,
  Tabs,
  Badge,
  Card,
  Statistic,
  Row,
  Col
} from 'antd';
import { 
  UploadOutlined, 
  FileOutlined, 
  CheckCircleOutlined, 
  ExclamationCircleOutlined,
  UserOutlined,
  ShoppingCartOutlined
} from '@ant-design/icons';
import type { UploadFile, UploadProps } from 'antd/es/upload/interface';
import apiClient from '../utils/axiosConfig.ts';
import { formatCurrency } from '../utils/numberFormatting.ts';

const { Title, Text, Paragraph } = Typography;

interface CustomerOrderImportModalProps {
  visible: boolean;
  onCancel: () => void;
  onSuccess: () => void;
}

interface ImportData {
  file_type: string;
  status: string;
  message: string;
  order_data: {
    customer_code: string;
    shipping_destination_name: string;
    order_date: string;
    order_number: string;
    customer_order_number: string;
    requested_delivery_date: string;
  };
  products: any[];
  total_amount: number;
  coordinate_mapping: any;
}

interface ValidationResult {
  customer: {
    id: number;
    company: string;
    customer_code: string;
  } | null;
  shipping_destination: {
    id: number;
    name: string;
  } | null;
  total_products: number;
  valid_products: number;
  order_errors: string[];
  product_errors: any[];
  warnings: string[];
  can_proceed: boolean;
}

interface ImportResult {
  success: boolean;
  order: {
    id: number;
    order_number: string;
    customer_order_number: string;
    customer_name: string;
    order_date: string;
    total_amount: number;
    products_count: number;
  };
  customer: {
    id: number;
    company: string;
    customer_code: string;
  };
  shipping_destination: {
    id: number;
    name: string;
  } | null;
  created_details: any[];
}

const CustomerOrderImportModal: React.FC<CustomerOrderImportModalProps> = ({
  visible,
  onCancel,
  onSuccess
}) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [fileList, setFileList] = useState<UploadFile[]>([]);
  const [importData, setImportData] = useState<ImportData | null>(null);
  const [validationResult, setValidationResult] = useState<ValidationResult | null>(null);
  const [importResult, setImportResult] = useState<ImportResult | null>(null);

  // Reset stato quando si chiude la modal
  const handleCancel = () => {
    setCurrentStep(0);
    setFileList([]);
    setImportData(null);
    setValidationResult(null);
    setImportResult(null);
    onCancel();
  };

  // Gestione upload file
  const uploadProps: UploadProps = {
    name: 'file',
    accept: '.xls,.xlsx',
    beforeUpload: () => false, // Previeni upload automatico
    onChange: ({ fileList: newFileList }) => {
      setFileList(newFileList);
    },
    fileList,
  };

  // Fase 1: Upload e parsing file
  const handleFileUpload = async () => {
    if (fileList.length === 0) {
      message.error('Seleziona un file da importare');
      return;
    }

    const file = fileList[0].originFileObj;
    if (!file) return;

    console.log('=== CUSTOMER ORDER IMPORT UPLOAD DEBUG ===');
    console.log('File selected:', {
      name: file.name,
      size: file.size,
      type: file.type,
      lastModified: file.lastModified
    });

    setLoading(true);
    
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      console.log('Making request to: /customer-order-import/upload');
      const response = await apiClient.post('/customer-order-import/upload', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      
      console.log('Response received:', {
        status: response.status,
        statusText: response.statusText,
        data: response.data
      });

      const data: ImportData = response.data;
      console.log('Upload success:', data);
      setImportData(data);

      if (data.status === 'success') {
        message.success(data.message);
        setCurrentStep(1);
      }
    } catch (error: any) {
      console.error('Upload error:', error);
      const errorMessage = error.response?.data?.detail || error.message || 'Errore durante il parsing del file';
      message.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  // Fase 2: Validazione dati ordine
  const handleValidation = async () => {
    if (!importData) return;

    setLoading(true);
    
    try {
      const response = await apiClient.post('/customer-order-import/validate', { 
        order_data: importData.order_data,
        products: importData.products 
      });

      const result: ValidationResult = response.data;
      setValidationResult(result);

      if (result.can_proceed) {
        setCurrentStep(2);
      } else {
        console.error('Validation errors:', result.order_errors, result.product_errors);
        // Mostra errori di validazione
        setCurrentStep(1.5); // Stato intermedio per mostrare errori
      }
    } catch (error: any) {
      console.error('Validation error:', error);
      const errorMessage = error.response?.data?.detail || error.message || 'Errore durante la validazione';
      message.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  // Fase 3: Esecuzione import
  const handleImportExecution = async () => {
    if (!importData || !validationResult?.can_proceed) return;

    setLoading(true);
    
    try {
      const response = await apiClient.post('/customer-order-import/execute', { 
        order_data: importData.order_data,
        products: importData.products,
        total_amount: importData.total_amount,
        filename: fileList[0]?.name || 'unknown'
      });

      const result: ImportResult = response.data;
      setImportResult(result);

      if (result.success) {
        message.success(`Ordine "${result.order.order_number}" importato con successo con ${result.order.products_count} prodotti`);
        onSuccess();
      } else {
        message.error('Errore durante l\'importazione dell\'ordine');
      }
      
      setCurrentStep(3);
    } catch (error: any) {
      console.error('Import execution error:', error);
      const errorMessage = error.response?.data?.detail || error.message || 'Errore durante l\'importazione';
      message.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  // Naviga tra le fasi
  const goToStep = (step: number) => {
    if (step <= Math.floor(currentStep) || step === 0) {
      setCurrentStep(step);
    }
  };

  // Definizione dei passi
  const steps = [
    {
      title: 'Seleziona File',
      icon: <UploadOutlined />,
    },
    {
      title: 'Anteprima Ordine', 
      icon: <FileOutlined />,
    },
    {
      title: 'Conferma Importazione',
      icon: <CheckCircleOutlined />,
    },
    {
      title: 'Risultato',
      icon: <ExclamationCircleOutlined />,
    },
  ];

  // Rendering contenuto per ogni fase
  const renderStepContent = () => {
    switch (currentStep) {
      case 0:
        return (
          <div style={{ textAlign: 'center', padding: '40px 0' }}>
            <Upload.Dragger {...uploadProps} style={{ marginBottom: 24 }}>
              <p className="ant-upload-drag-icon">
                <UploadOutlined />
              </p>
              <p className="ant-upload-text">Clicca o trascina il file XLS in quest'area per caricarlo</p>
              <p className="ant-upload-hint">
                Supportati: file XLS, XLSX con struttura standardizzata
              </p>
            </Upload.Dragger>
            
            <Alert
              message="Struttura File Richiesta"
              description={
                <div style={{ textAlign: 'left' }}>
                  <p><strong>Coordinate fisse richieste:</strong></p>
                  <ul>
                    <li>F17: Codice Cliente</li>
                    <li>J11: Nome Destinazione Spedizione</li>
                    <li>C17: Data Ordine</li>
                    <li>A17: Numero Ordine</li>
                    <li>I17: Numero Ordine Cliente</li>
                    <li>Q25: Data Consegna Richiesta</li>
                    <li>A25+: Codice Prodotto (righe successive)</li>
                    <li>K25+: Quantità (righe successive)</li>
                    <li>L25+: Prezzo Unitario (righe successive)</li>
                  </ul>
                </div>
              }
              type="info"
              style={{ marginBottom: 24, textAlign: 'left' }}
            />
            
            <Space>
              <Button 
                type="primary" 
                onClick={handleFileUpload}
                loading={loading}
                disabled={fileList.length === 0}
              >
                Analizza File
              </Button>
            </Space>
          </div>
        );

      case 1:
        if (!importData) return null;
        
        return (
          <div>
            <Alert
              message={`File parsato con successo: ${importData.products.length} prodotti trovati`}
              type="success"
              style={{ marginBottom: 24 }}
            />
            
            <Row gutter={16} style={{ marginBottom: 24 }}>
              <Col span={12}>
                <Card title="Dati Ordine" size="small">
                  <Descriptions column={1} size="small">
                    <Descriptions.Item label="Numero Ordine">
                      <strong>{importData.order_data.order_number}</strong>
                    </Descriptions.Item>
                    <Descriptions.Item label="Numero Ordine Cliente">
                      {importData.order_data.customer_order_number || 'N/A'}
                    </Descriptions.Item>
                    <Descriptions.Item label="Data Ordine">
                      {importData.order_data.order_date}
                    </Descriptions.Item>
                    <Descriptions.Item label="Data Consegna">
                      {importData.order_data.requested_delivery_date || 'N/A'}
                    </Descriptions.Item>
                  </Descriptions>
                </Card>
              </Col>
              <Col span={12}>
                <Card title="Cliente e Spedizione" size="small">
                  <Descriptions column={1} size="small">
                    <Descriptions.Item label="Codice Cliente">
                      <strong>{importData.order_data.customer_code}</strong>
                    </Descriptions.Item>
                    <Descriptions.Item label="Destinazione">
                      {importData.order_data.shipping_destination_name || 'N/A'}
                    </Descriptions.Item>
                    <Descriptions.Item label="Totale Ordine">
                      <strong>€ {formatCurrency(importData.total_amount)}</strong>
                    </Descriptions.Item>
                    <Descriptions.Item label="Prodotti">
                      {importData.products.length}
                    </Descriptions.Item>
                  </Descriptions>
                </Card>
              </Col>
            </Row>

            <Title level={4}>Anteprima Prodotti:</Title>
            <Table
              dataSource={importData.products.slice(0, 5).map((product, index) => ({
                key: index,
                ...product
              }))}
              columns={[
                { title: 'Riga', dataIndex: 'row', key: 'row', width: 60 },
                { title: 'Codice Prodotto', dataIndex: 'product_code', key: 'product_code', width: 120 },
                { title: 'Quantità', dataIndex: 'quantity', key: 'quantity', width: 80 },
                { title: 'Vendita Unit.', dataIndex: 'unit_price', key: 'unit_price', width: 100, render: (value: number) => `€ ${formatCurrency(value)}` },
                { title: 'Totale', dataIndex: 'total_price', key: 'total_price', width: 100, render: (value: number) => `€ ${formatCurrency(value)}` }
              ]}
              pagination={false}
              size="small"
              scroll={{ x: 500 }}
            />

            {importData.products.length > 5 && (
              <Text type="secondary" style={{ display: 'block', marginTop: 8 }}>
                Mostrati i primi 5 prodotti. Totale: {importData.products.length}
              </Text>
            )}

            <Title level={4} style={{ marginTop: 24 }}>Mappatura Coordinate:</Title>
            <Table
              dataSource={Object.entries(importData.coordinate_mapping).map(([coordinate, description], index) => ({
                key: index,
                coordinate,
                description
              }))}
              columns={[
                { title: 'Coordinata', dataIndex: 'coordinate', key: 'coordinate', width: 120 },
                { title: 'Campo', dataIndex: 'description', key: 'description' }
              ]}
              pagination={false}
              size="small"
            />

            <div style={{ marginTop: 24, textAlign: 'center' }}>
              <Button type="primary" onClick={handleValidation} loading={loading}>
                Valida Dati e Procedi
              </Button>
            </div>
          </div>
        );

      case 1.5:
        if (!validationResult) return null;
        
        return (
          <div>
            <Alert
              message={`Validazione completata: ${validationResult.valid_products} prodotti validi di ${validationResult.total_products}`}
              type={validationResult.can_proceed ? "warning" : "error"}
              style={{ marginBottom: 24 }}
            />

            {validationResult.order_errors.length > 0 && (
              <>
                <Title level={4}>Errori Ordine:</Title>
                <Alert
                  message="Errori nei dati dell'ordine"
                  description={
                    <ul>
                      {validationResult.order_errors.map((error, index) => (
                        <li key={index}>{error}</li>
                      ))}
                    </ul>
                  }
                  type="error"
                  style={{ marginBottom: 24 }}
                />
              </>
            )}

            {validationResult.product_errors.length > 0 && (
              <>
                <Title level={4}>Errori Prodotti:</Title>
                <Table
                  dataSource={validationResult.product_errors.map((error, index) => ({
                    key: index,
                    row: error.row,
                    product_code: error.product_code,
                    errors: error.errors.join(', ')
                  }))}
                  columns={[
                    { title: 'Riga', dataIndex: 'row', key: 'row', width: 80 },
                    { title: 'Codice Prodotto', dataIndex: 'product_code', key: 'product_code', width: 150 },
                    { title: 'Errori', dataIndex: 'errors', key: 'errors' }
                  ]}
                  pagination={{ pageSize: 10 }}
                  size="small"
                  style={{ marginBottom: 24 }}
                />
              </>
            )}

            {validationResult.warnings.length > 0 && (
              <>
                <Title level={4}>Avvisi:</Title>
                <Alert
                  message="Avvisi"
                  description={
                    <ul>
                      {validationResult.warnings.map((warning, index) => (
                        <li key={index}>{warning}</li>
                      ))}
                    </ul>
                  }
                  type="warning"
                  style={{ marginBottom: 24 }}
                />
              </>
            )}

            <Space>
              <Button onClick={() => setCurrentStep(1)}>
                Indietro
              </Button>
              {validationResult.can_proceed && (
                <Button 
                  type="primary" 
                  onClick={() => setCurrentStep(2)}
                >
                  Procedi con l'Importazione
                </Button>
              )}
            </Space>
          </div>
        );

      case 2:
        if (!validationResult || !importData) return null;

        return (
          <div>
            <Title level={4}>Conferma Importazione Ordine</Title>
            
            <Alert
              message={`Pronto per importare l'ordine con ${validationResult.valid_products} prodotti`}
              type="info"
              style={{ marginBottom: 24 }}
            />

            <Row gutter={16} style={{ marginBottom: 24 }}>
              <Col span={8}>
                <Card>
                  <Statistic
                    title="Cliente"
                    value={validationResult.customer?.company || 'Non trovato'}
                    prefix={<UserOutlined />}
                    valueStyle={{ fontSize: '16px' }}
                  />
                  <Text type="secondary">Codice: {validationResult.customer?.customer_code}</Text>
                </Card>
              </Col>
              <Col span={8}>
                <Card>
                  <Statistic
                    title="Prodotti Validi"
                    value={validationResult.valid_products}
                    prefix={<ShoppingCartOutlined />}
                    suffix={`di ${validationResult.total_products}`}
                  />
                </Card>
              </Col>
              <Col span={8}>
                <Card>
                  <Statistic
                    title="Totale Ordine"
                    value={importData.total_amount}
                    precision={2}
                    prefix="€"
                  />
                </Card>
              </Col>
            </Row>

            {validationResult.shipping_destination ? (
              <Alert
                message={`Destinazione "${validationResult.shipping_destination.name}" trovata`}
                type="success"
                style={{ marginBottom: 24 }}
              />
            ) : (
              <Alert
                message={`Destinazione "${importData.order_data.shipping_destination_name}" verrà creata automaticamente`}
                type="info"
                style={{ marginBottom: 24 }}
              />
            )}

            {validationResult.warnings.length > 0 && (
              <Alert
                message="Avvisi"
                description={
                  <ul>
                    {validationResult.warnings.map((warning, index) => (
                      <li key={index}>{warning}</li>
                    ))}
                  </ul>
                }
                type="warning"
                style={{ marginBottom: 24 }}
              />
            )}

            <Space>
              <Button onClick={() => setCurrentStep(1)}>
                Indietro
              </Button>
              <Button 
                type="primary" 
                onClick={handleImportExecution}
                loading={loading}
              >
                Importa Ordine
              </Button>
            </Space>
          </div>
        );

      case 3:
        if (!importResult) return null;

        return (
          <div>
            <Result
              status="success"
              title="Ordine Importato con Successo!"
              subTitle={
                <div>
                  <p>Ordine <strong>{importResult.order.order_number}</strong> creato per il cliente <strong>{importResult.customer.company}</strong></p>
                  <p>Importati {importResult.order.products_count} prodotti per un totale di € {formatCurrency(importResult.order.total_amount)}</p>
                </div>
              }
            />

            <Card title="Dettagli Ordine" style={{ marginBottom: 24 }}>
              <Descriptions column={2} bordered>
                <Descriptions.Item label="ID Ordine">
                  {importResult.order.id}
                </Descriptions.Item>
                <Descriptions.Item label="Numero Ordine">
                  {importResult.order.order_number}
                </Descriptions.Item>
                <Descriptions.Item label="Numero Ordine Cliente">
                  {importResult.order.customer_order_number || 'N/A'}
                </Descriptions.Item>
                <Descriptions.Item label="Data Ordine">
                  {importResult.order.order_date}
                </Descriptions.Item>
                <Descriptions.Item label="Cliente">
                  {importResult.customer.company}
                </Descriptions.Item>
                <Descriptions.Item label="Codice Cliente">
                  {importResult.customer.customer_code}
                </Descriptions.Item>
                <Descriptions.Item label="Destinazione">
                  {importResult.shipping_destination?.name || 'Creata automaticamente'}
                </Descriptions.Item>
                <Descriptions.Item label="Prodotti">
                  {importResult.order.products_count}
                </Descriptions.Item>
              </Descriptions>
            </Card>

            <Title level={4}>Prodotti Importati:</Title>
            <Table
              dataSource={importResult.created_details.map((detail, index) => ({
                key: index,
                ...detail
              }))}
              columns={[
                { title: 'ID Prodotto', dataIndex: 'product_id', key: 'product_id', width: 100 },
                { title: 'Codice', dataIndex: 'product_code', key: 'product_code', width: 120 },
                { title: 'Descrizione', dataIndex: 'product_description', key: 'product_description' },
                { title: 'Quantità', dataIndex: 'quantity', key: 'quantity', width: 80 },
                { title: 'Vendita Unit.', dataIndex: 'unit_price', key: 'unit_price', width: 100, render: (value: number) => `€ ${formatCurrency(value)}` }
              ]}
              pagination={{ pageSize: 10 }}
              size="small"
              scroll={{ x: 600 }}
            />

            <div style={{ textAlign: 'center', marginTop: 24 }}>
              <Button type="primary" onClick={handleCancel}>
                Chiudi
              </Button>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <Modal
      title="Importazione Ordine Cliente da XLS"
      open={visible}
      onCancel={handleCancel}
      footer={null}
      width={900}
      destroyOnClose
    >
      <Spin spinning={loading}>
        <Steps 
          current={Math.floor(currentStep)} 
          items={steps}
          style={{ marginBottom: 32 }}
          onChange={goToStep}
        />
        
        {renderStepContent()}
      </Spin>
    </Modal>
  );
};

export default CustomerOrderImportModal;